/**
 * Tool / platform logo registry.
 *
 * The SVGs live in /public/assets/logos and were pulled from the Simple Icons
 * CDN (brand-coloured, with a light override for brands whose mark is black so
 * they stay legible on the dark canvas). Two marks are local:
 *   - gohighlevel.svg — hand-authored stand-in (HighLevel ships no open SVG)
 *   - charyxlabs.svg  — the Charyx Labs app mark from charyxlabs.tech
 */

export type LogoSlug =
  // automation & CRM
  | 'n8n'
  | 'make'
  | 'zapier'
  | 'gohighlevel'
  | 'airtable'
  | 'googlesheets'
  | 'slack'
  | 'notion'
  | 'calendly'
  | 'hubspot'
  // ai & agents
  | 'anthropic'
  | 'openai'
  | 'googlegemini'
  | 'langchain'
  | 'elevenlabs'
  | 'huggingface'
  | 'opencv'
  | 'tensorflow'
  // comms & growth
  | 'twilio'
  | 'whatsapp'
  | 'telegram'
  | 'gmail'
  | 'meta'
  | 'instagram'
  | 'googleads'
  | 'googleanalytics'
  // code & data
  | 'python'
  | 'fastapi'
  | 'flask'
  | 'react'
  | 'typescript'
  | 'nodedotjs'
  | 'nextdotjs'
  | 'tailwindcss'
  | 'postgresql'
  | 'mongodb'
  | 'redis'
  | 'supabase'
  | 'selenium'
  // infra & commerce
  | 'docker'
  | 'vercel'
  | 'netlify'
  | 'googlecloud'
  | 'amazonaws'
  | 'stripe'
  | 'wordpress'
  | 'shopify'
  | 'git'
  | 'github'
  // studio
  | 'charyxlabs';

/** Display name for each slug (the file name is always `${slug}.svg`). */
export const LOGO_NAMES: Record<LogoSlug, string> = {
  n8n: 'n8n',
  make: 'Make',
  zapier: 'Zapier',
  gohighlevel: 'GoHighLevel',
  airtable: 'Airtable',
  googlesheets: 'Google Sheets',
  slack: 'Slack',
  notion: 'Notion',
  calendly: 'Calendly',
  hubspot: 'HubSpot',

  anthropic: 'Claude',
  openai: 'OpenAI',
  googlegemini: 'Gemini',
  langchain: 'LangChain',
  elevenlabs: 'ElevenLabs',
  huggingface: 'Hugging Face',
  opencv: 'OpenCV',
  tensorflow: 'TensorFlow',

  twilio: 'Twilio',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  gmail: 'Gmail',
  meta: 'Meta Ads',
  instagram: 'Instagram',
  googleads: 'Google Ads',
  googleanalytics: 'GA4',

  python: 'Python',
  fastapi: 'FastAPI',
  flask: 'Flask',
  react: 'React',
  typescript: 'TypeScript',
  nodedotjs: 'Node.js',
  nextdotjs: 'Next.js',
  tailwindcss: 'Tailwind',
  postgresql: 'PostgreSQL',
  mongodb: 'MongoDB',
  redis: 'Redis',
  supabase: 'Supabase',
  selenium: 'Selenium',

  docker: 'Docker',
  vercel: 'Vercel',
  netlify: 'Netlify',
  googlecloud: 'Google Cloud',
  amazonaws: 'AWS',
  stripe: 'Stripe',
  wordpress: 'WordPress',
  shopify: 'Shopify',
  git: 'Git',
  github: 'GitHub',

  charyxlabs: 'Charyx Labs',
};

export const logoSrc = (slug: LogoSlug) => `/assets/logos/${slug}.svg`;

/** The two rails rendered under the hero — split so both read as full rows. */
export const RAIL_TOP: LogoSlug[] = [
  'n8n',
  'gohighlevel',
  'make',
  'zapier',
  'anthropic',
  'openai',
  'googlegemini',
  'langchain',
  'elevenlabs',
  'twilio',
  'slack',
  'airtable',
  'googlesheets',
  'notion',
  'calendly',
  'hubspot',
];

export const RAIL_BOTTOM: LogoSlug[] = [
  'python',
  'fastapi',
  'flask',
  'react',
  'typescript',
  'nodedotjs',
  'postgresql',
  'mongodb',
  'redis',
  'supabase',
  'docker',
  'googlecloud',
  'amazonaws',
  'stripe',
  'shopify',
  'wordpress',
  'whatsapp',
  'meta',
  'googleads',
  'googleanalytics',
];
